import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../../../theming/useTheme';
import { TextLinkComponent, ThemedLinkStyle, generateLinkStyleFromTheme } from '../../../../partials/link/TextLink';
import { HeaderLinksDataProps } from '../Header';
import { notActiveLinkIdleColor } from '../header.utils';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { Span, semanticCSS } from '../../../../../typography/semanticTags';
import {
  FontSizesStyles,
  getStyleBySize,
  SemanticTagThemeProps,
} from '../../../../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'primaryLinks';

export interface PrimaryLinksThemeProps extends SemanticTagThemeProps {
  activeBorderBottom: string
  hoverOtherLinksColors: string
  baseUnit: BaseUnit
  idleColor: string,
  activeStateColor: string,
  hoverStateColor: string,
}

interface state {
  isHoveredLink: boolean
}

const radiusBorder = 4;

const getLinkStyle = (activeBorderBottom: string, isActive: boolean = false) => {
  return StyleSheet.create({
    link: {
      height: '100%',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      ':before': isActive ? {
        height: `${radiusBorder}px`,
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        content: "''",
        backgroundColor: activeBorderBottom,
      } : {},
    },
  });
};

const getContainerStyle = (baseUnit: BaseUnit) => {
  return StyleSheet.create({
    linksContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      gridColumnGap: `${baseUnit}px`,
    },
  });
};

export interface PrimaryLinksDataProps {
  links: HeaderLinksDataProps[]
  pagePath: string
}

export type PrimaryLinksComponentProps = PrimaryLinksDataProps & PrimaryLinksThemeProps;

const additionalStyle = {
  [MEDIA_BREAKPOINTS.LARGE]: {
    display: 'block',
  },
  [MEDIA_BREAKPOINTS.MEDIUM]: {
    display: 'none',
  },
  [MEDIA_BREAKPOINTS.SMALL]: {
    display: 'none',
  },
};

const getContentStyle = (isActiveLink: boolean, fontSizesStyles: FontSizesStyles) => {
  const fontSizesActive = { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER };
  const fontSizesNotActive = { fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL, fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL, fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL };
  return isActiveLink ? getStyleBySize(fontSizesStyles, fontSizesActive) : getStyleBySize(fontSizesStyles, fontSizesNotActive);
};

const createLink = ({ idleColor, activeStateColor, hoverStateColor, activeBorderBottom, hoverOtherLinksColors, fontSizesStyles }: ThemedLinkStyle & PrimaryLinksThemeProps, isHoveredLink: boolean, pagePath: string) => (
  (linkData: HeaderLinksDataProps, index: number) => {
    const isActiveLink = linkData.href === pagePath;
    const style = getLinkStyle(activeBorderBottom, isActiveLink);
    const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
    const linkStyle = generateLinkStyleFromTheme({ idleColor: calculatedIdleColor, activeStateColor, hoverStateColor }, additionalStyle);
    const contentStyle = getContentStyle(isActiveLink, fontSizesStyles);
    return (
      <TextLinkComponent key={index} href={linkData.href} linkStyle={linkStyle}>
        <Span styles={semanticCSS(contentStyle, style.link)}>{linkData.text}</Span>
      </TextLinkComponent>
    );
  }
);


export const PrimaryLinksComponent: React.FunctionComponent<PrimaryLinksComponentProps> = props => {
  const [isHoveredLink, setIsHoveredLink] = useState(false);
  const { links, baseUnit, pagePath } = props;
  const style = getContainerStyle(baseUnit);
  return (
    <div className={css(style.linksContainer)} onMouseEnter={() => setIsHoveredLink(true)} onMouseLeave={() => setIsHoveredLink(false)}>
      {links.map(createLink(props, isHoveredLink, pagePath))}
    </div>
  );
};


const mapThemeProps = (theme: PrimaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    activeBorderBottom: theme.activeBorderBottom,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    baseUnit: theme.baseUnit,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const PrimaryLinks = (props: PrimaryLinksDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ErrorBoundary><PrimaryLinksComponent {...themeContext} {...props} /></ErrorBoundary>;
};
